import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

interface TrackingSlide {
  id: string;
  title: string;
  description: string | null;
  image_url: string | null;
  link_url: string | null;
}

interface TrackingPromoSliderProps {
  slides: TrackingSlide[];
  primaryColor?: string;
}

export const TrackingPromoSlider = ({ slides, primaryColor = "#f97316" }: TrackingPromoSliderProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    if (slides.length <= 1) return;
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % slides.length);
    }, 4000);
    return () => clearInterval(intervalRef.current);
  }, [slides.length]);

  if (slides.length === 0) return null;

  const slide = slides[currentIndex];

  return (
    <div className="rounded-2xl overflow-hidden bg-white border border-[hsl(0,0%,92%)] shadow-sm">
      <AnimatePresence mode="wait">
        <motion.div
          key={slide.id}
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -30 }}
          transition={{ duration: 0.3 }}
        >
          {slide.link_url ? (
            <a href={slide.link_url} target="_blank" rel="noopener noreferrer" className="block">
              <SlideContent slide={slide} />
            </a>
          ) : (
            <SlideContent slide={slide} />
          )}
        </motion.div>
      </AnimatePresence>

      {/* Dots */}
      {slides.length > 1 && (
        <div className="flex gap-1.5 justify-center pb-3">
          {slides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className="w-2 h-2 rounded-full transition-all"
              style={{
                backgroundColor: idx === currentIndex ? primaryColor : `${primaryColor}30`,
                width: idx === currentIndex ? "16px" : "8px",
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const SlideContent = ({ slide }: { slide: TrackingSlide }) => (
  <div>
    {slide.image_url && (
      <div className="w-full h-36 overflow-hidden">
        <img src={slide.image_url} alt={slide.title} className="w-full h-full object-cover" />
      </div>
    )}
    <div className="p-3">
      <p className="font-bold text-sm text-[hsl(0,0%,13%)]">{slide.title}</p>
      {slide.description && (
        <p className="text-xs text-[hsl(0,0%,55%)] mt-0.5">{slide.description}</p>
      )}
    </div>
  </div>
);
